/**
 * UI slice for managing theme, layout, notifications, and modals
 * Refactored from the original UI store to follow the slice pattern
 */

import type { StateCreator } from 'zustand';
import type { UISlice, AppStore, Notification, Modal } from '../types';

let notificationIdCounter = 0;
let modalIdCounter = 0;

export const createUISlice: StateCreator<AppStore, [], [], UISlice> = (set, get) => ({
  // Initial state
  theme: 'system',
  sidebarState: 'expanded',
  isMobileMenuOpen: false,
  isCommandPaletteOpen: false,
  notifications: [],
  modals: [],
  globalLoading: false,
  loadingMessage: undefined,

  // Theme actions
  setTheme: (theme) => {
    set({ theme });

    // Apply theme to document
    const root = document.documentElement;

    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', prefersDark);
    } else {
      root.classList.toggle('dark', theme === 'dark');
    }
  },

  // Sidebar actions
  toggleSidebar: () => {
    const currentState = get().sidebarState;
    const newState = currentState === 'expanded' ? 'collapsed' : 'expanded';
    set({ sidebarState: newState });
  },

  setSidebarState: (sidebarState) => {
    set({ sidebarState });
  },

  // Mobile menu actions
  toggleMobileMenu: () =>
    set((state) => ({
      isMobileMenuOpen: !state.isMobileMenuOpen,
    })),

  setMobileMenuOpen: (isMobileMenuOpen) => set({ isMobileMenuOpen }),

  // Command palette actions
  toggleCommandPalette: () =>
    set((state) => ({
      isCommandPaletteOpen: !state.isCommandPaletteOpen,
    })),

  setCommandPaletteOpen: (isCommandPaletteOpen) => set({ isCommandPaletteOpen }),

  // Notification actions
  addNotification: (notification) => {
    const id = `notification-${++notificationIdCounter}`;
    const timestamp = Date.now();

    const newNotification: Notification = {
      ...notification,
      id,
      timestamp,
    };

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-remove notification after duration
    if (notification.duration && notification.duration > 0) {
      setTimeout(() => {
        get().removeNotification(id);
      }, notification.duration);
    }
  },

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  clearNotifications: () => set({ notifications: [] }),

  // Modal actions
  openModal: (modal) => {
    const id = `modal-${++modalIdCounter}`;

    const newModal: Modal = {
      ...modal,
      id,
    };

    set((state) => ({
      modals: [...state.modals, newModal],
    }));

    // Return the modal ID for reference
    return id;
  },

  closeModal: (id) => {
    const modal = get().modals.find((m) => m.id === id);

    if (modal?.onClose) {
      modal.onClose();
    }

    set((state) => ({
      modals: state.modals.filter((m) => m.id !== id),
    }));
  },

  closeAllModals: () => {
    const modals = get().modals;

    // Call onClose for all modals
    modals.forEach((modal) => {
      if (modal.onClose) {
        modal.onClose();
      }
    });

    set({ modals: [] });
  },

  // Loading actions
  setGlobalLoading: (loading, message) =>
    set({
      globalLoading: loading,
      loadingMessage: message,
    }),
});

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    // Only update if theme is set to 'system'
    const state = localStorage.getItem('sendguard-preferences');
    if (state) {
      try {
        const parsed = JSON.parse(state);
        if (parsed.state?.theme === 'system') {
          document.documentElement.classList.toggle('dark', e.matches);
        }
      } catch {
        // Ignore parsing errors
      }
    }
  });
}
